import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { ApiError, api, type Meta, type Ticket } from '../api';

const OPTIONAL: { key: string; label: string; options: keyof Meta }[] = [
  { key: 'status', label: 'Status', options: 'statuses' },
  { key: 'planPriority', label: 'Plan priority', options: 'planPriorities' },
  { key: 'sequencing', label: 'Sequencing', options: 'sequencings' },
  { key: 'effort', label: 'Effort', options: 'efforts' },
];

const EMPTY = {
  name: '',
  epic: '',
  revenueConsequence: '',
  body: '',
  status: '',
  planPriority: '',
  sequencing: '',
  effort: '',
  source: '',
};

type Fields = typeof EMPTY;

/**
 * Each of these is left blank by default and dropped from the payload rather than sent
 * as an empty string, so the server's own defaults apply — which is why "default" is a
 * real option here rather than a placeholder.
 */
function OptionalFields({
  fields,
  set,
  meta,
}: {
  fields: Fields;
  set: (key: string, value: string) => void;
  meta: Meta | null;
}) {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
      {OPTIONAL.map((field) => (
        <TextField
          key={field.key}
          select
          size="small"
          label={field.label}
          sx={{ minWidth: 150 }}
          value={fields[field.key as keyof Fields]}
          onChange={(event) => set(field.key, event.target.value)}
        >
          <MenuItem value="">
            <em>default</em>
          </MenuItem>
          {(meta?.[field.options] ?? []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      ))}
    </Stack>
  );
}

function TicketForm({
  fields,
  set,
  errors,
  meta,
}: {
  fields: Fields;
  set: (key: string, value: string) => void;
  errors: string[];
  meta: Meta | null;
}) {
  return (
      <Stack spacing={2} sx={{ mt: 0.5 }}>
        {errors.length > 0 && (
          <Alert severity="error">
            {errors.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </Alert>
        )}

        <TextField
          label="Name"
          size="small"
          required
          value={fields.name}
          onChange={(event) => set('name', event.target.value)}
        />

        <TextField
          select
          label="Epic"
          size="small"
          required
          value={fields.epic}
          onChange={(event) => set('epic', event.target.value)}
        >
          {(meta?.epics ?? []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Revenue consequence"
          size="small"
          required
          multiline
          minRows={2}
          helperText="What this unlocks or blocks commercially — not what the feature does."
          value={fields.revenueConsequence}
          onChange={(event) => set('revenueConsequence', event.target.value)}
        />

        <OptionalFields fields={fields} set={set} meta={meta} />

        <TextField
          label="Source"
          size="small"
          placeholder="Repo issue, brain note, wherever this came from"
          value={fields.source}
          onChange={(event) => set('source', event.target.value)}
        />

        <TextField
          label="Body (markdown)"
          size="small"
          multiline
          minRows={6}
          helperText="Problem, acceptance criteria, and a link to the vault note."
          value={fields.body}
          onChange={(event) => set('body', event.target.value)}
        />
      </Stack>
  );
}

export function NewTicketDialog({
  open,
  meta,
  onClose,
  onCreated,
}: {
  open: boolean;
  meta: Meta | null;
  onClose: () => void;
  onCreated: (ticket: Ticket) => void;
}) {
  const [fields, setFields] = useState({ ...EMPTY });
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const set = (key: string, value: string) => setFields((current) => ({ ...current, [key]: value }));

  async function submit() {
    setBusy(true);
    setErrors([]);
    try {
      // Blank optional fields are dropped rather than sent as '' — the API rejects an
      // empty string against an enum, and the server's own defaults are the right answer.
      const payload = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== ''));
      const result = await api.create(payload as Partial<Ticket>);
      onCreated(result.ticket);
      setFields({ ...EMPTY });
      onClose();
    } catch (cause) {
      // The API returns every problem at once; showing one at a time would turn a
      // mistyped form into four round trips.
      if (cause instanceof ApiError && cause.fieldErrors.length) setErrors(cause.fieldErrors);
      else setErrors([cause instanceof Error ? cause.message : 'Could not create the ticket.']);
    } finally {
      setBusy(false);
    }
  }

  const ready = fields.name.trim() && fields.epic && fields.revenueConsequence.trim();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New ticket</DialogTitle>
      <DialogContent>
        <TicketForm fields={fields} set={set} errors={errors} meta={meta} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy || !ready} onClick={() => void submit()}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
